import { Pressable, Text, View } from 'react-native'
import { useSafeAreaFrame, useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'

import { SERVER_DOMAIN } from '@/constants/config'
import { cn } from '@/utils/cn'
import { useRouter } from '@/utils/navigation'
import { useScanQrCodeFromImage } from '@/hooks/useScanQrCodeFromImage'
import { Button, ModalBody, ModalFooter, ModalHeader, ModalTitle, useToast } from '@/components/base'
import { ArrowBack } from '@/components/icon'
import ScreenHeader from '@/components/ScreenHeader'

const headerHeight = 44
const maskSquare = 300

export function useScanHandler(onAccepted?: () => void) {
  const { t } = useTranslation()
  const toast = useToast()
  const router = useRouter()

  function handleScanCode(code: string) {
    const route = getScan2PayRoute(code)
    if (!route) {
      console.log(`${code} is not a supported Scan2Pay URL for ${SERVER_DOMAIN}`)
      toast.show({ variant: 'warning', description: t('ui:unsupportedQrCode') })
      return
    }
    console.log('scan2pay handleScanCode', { raw: code, route })
    onAccepted?.()
    router.navigate(route)
  }

  return handleScanCode
}

export function getScan2PayRoute(code: string) {
  try {
    const url = new URL(code)
    const isBaobiHost =
      Boolean(SERVER_DOMAIN) && (url.hostname === SERVER_DOMAIN || url.hostname.endsWith(`.${SERVER_DOMAIN}`))
    const isPaymentRoute = url.pathname === '/payment' && Boolean(url.searchParams.get('systemId'))

    if (!['http:', 'https:'].includes(url.protocol) || !isBaobiHost || !isPaymentRoute) return null
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return null
  }
}

export function ScanHeader() {
  const { t } = useTranslation()
  const toast = useToast()
  const { handleAddQrCode } = useScanQrCodeFromImage()
  const router = useRouter()
  const { top } = useSafeAreaInsets()
  const handleScanCode = useScanHandler()

  return (
    <View className={'absolute inset-x-0 top-0 z-10'} style={{ paddingTop: top }}>
      <ScreenHeader
        leftElement={
          <Pressable onPress={() => router.back()}>
            {({ pressed }) => <ArrowBack className={cn('size-6 text-white', pressed && 'opacity-50')} />}
          </Pressable>
        }
        centerElement={<Text className={'font-medium text-white'}>{t('ui:scanQrCode')}</Text>}
        rightElement={
          <Pressable
            accessibilityLabel={t('ui:scanQrFromLibrary')}
            accessibilityRole={'button'}
            testID={'scan2pay-photo-library'}
            onPress={() =>
              handleAddQrCode({
                onSuccess: handleScanCode,
                onError: () => toast.show({ variant: 'warning', description: t('ui:qrCodeNotFound') })
              })
            }
          >
            <Text className={'font-medium text-white'}>{t('ui:photoLibrary')}</Text>
          </Pressable>
        }
      />
    </View>
  )
}

export function ScannerMask({ ready }: { ready: boolean }) {
  const { height: withHeaderHeight } = useSafeAreaFrame()
  const height = withHeaderHeight - headerHeight
  return (
    <View className={'absolute inset-0'} pointerEvents={'none'}>
      <View className={'grow bg-black/50'} />
      <View className={'flex-row'} style={{ flexBasis: maskSquare }}>
        <View className={'grow bg-black/50'} />
        {ready && (
          <View className={'relative border-2 border-black/50'} style={{ flexBasis: maskSquare }}>
            <View className={'absolute -left-0.5 -top-0.5 size-8 border-l-2 border-t-2 border-white'} />
            <View className={'absolute -right-0.5 -top-0.5 size-8 border-r-2 border-t-2 border-white'} />
            <View className={'absolute -bottom-0.5 -left-0.5 size-8 border-b-2 border-l-2 border-white'} />
            <View className={'absolute -bottom-0.5 -right-0.5 size-8 border-b-2 border-r-2 border-white'} />
          </View>
        )}
        <View className={'grow bg-black/50'} />
      </View>
      <View className={'bg-black/50'} style={{ flexBasis: Math.max((height - maskSquare) / 2, 0) }} />
    </View>
  )
}

export function CameraUnavailable() {
  const { t } = useTranslation()
  const router = useRouter()
  return (
    <View className={'flex-1 items-center justify-center bg-black/50'}>
      <View className={'mx-4 w-full max-w-[360px] rounded-xl bg-background-primary shadow-3'}>
        <ModalHeader className={'p-4'}>
          <ModalTitle>{t('ui:cameraUnavailable')}</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <Text className={'text-center text-sm text-text-secondary'}>
            {t('ui:cameraPermissionDescription')}
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button className={'flex-1'} size={'md'} variant={'solid'} onPress={() => router.back()}>
            {t('ui:gotIt')}
          </Button>
        </ModalFooter>
      </View>
      <ScanHeader />
    </View>
  )
}
